// Singleton-Bus für die aktuelle GPS-Position.
//
// Motivation:
//   Mehrere Components wollen die aktuelle Position wissen (LocateControl für
//   Kamera-Zentrierung, ElevationBadge für Höhenanzeige, irgendwann auch
//   RidePage für Live-Tracking). watchPosition zweimal zu starten wäre Unsinn —
//   einer publisht, die anderen subscriben.
//
//   Der Publisher ist aktuell der LocateControl (nur wenn der User den
//   Locate-Button gedrückt hat und Permission gegeben hat). Solange niemand
//   den Button gedrückt hat, bleibt current = null und Reader-Components
//   zeigen entsprechend "keine Position" an.
//
//   Kein React-State: die Map-Control ist React-extern, und wir wollen
//   beidseitig reingehen können.

export type Listener = (pos: GeolocationCoordinates | null) => void;

class PositionBus {
  private current: GeolocationCoordinates | null = null;
  // ULTRA Test-Modus: simulierte Position überschreibt das echte GPS für ALLE
  // Reader (Pit-Stop, from-here-Routing, Locate). So testet man vor dem Rennen,
  // als wäre man woanders. null = aus (echtes GPS).
  private simulated: GeolocationCoordinates | null = null;
  private readonly listeners = new Set<Listener>();

  get(): GeolocationCoordinates | null {
    return this.simulated ?? this.current;
  }

  set(pos: GeolocationCoordinates | null): void {
    this.current = pos;
    // Im Sim-Modus sehen Reader weiter die simulierte Position.
    if (!this.simulated) for (const l of this.listeners) l(pos);
  }

  /** Test-Modus: simulierte Position setzen (null = zurück zu echtem GPS). */
  setSimulated(pos: GeolocationCoordinates | null): void {
    this.simulated = pos;
    const eff = this.get();
    for (const l of this.listeners) l(eff);
  }

  isSimulated(): boolean {
    return this.simulated != null;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }
}

export const positionBus = new PositionBus();

/** GeolocationCoordinates-ähnliches Objekt für den Sim-Modus. */
export function makeSimCoords(
  lat: number,
  lon: number,
  heading: number | null = null,
): GeolocationCoordinates {
  return {
    latitude: lat,
    longitude: lon,
    accuracy: 5,
    altitude: null,
    altitudeAccuracy: null,
    heading: heading == null ? NaN : heading,
    speed: heading == null ? null : 5,
  } as unknown as GeolocationCoordinates;
}
